<div
  class="preview-container"
  data-testid="preview-viewer"
  [attr.data-project-id]="currentPageProjectId || null"
  [attr.data-file-id]="currentPageIdentityFileId || null"
  [attr.data-page-number]="currentPageNumber || null"
  [attr.data-page-title]="currentPageTitle || null"
  [attr.data-render-state]="previewRenderState"
  [attr.aria-busy]="isLoading || isRotating"
>
  @if (findOpen) {
    <form class="preview-find" role="search" aria-label="Find text in preview" (submit)="$event.preventDefault()">
      <mat-icon aria-hidden="true">search</mat-icon>
      <label class="visually-hidden" for="preview-find-input">Find text in this file</label>
      <input
        #findInput
        id="preview-find-input"
        name="previewFind"
        type="search"
        autocomplete="off"
        placeholder="Find in file"
        [(ngModel)]="findQuery"
        (ngModelChange)="onFindQueryChange($event)"
        (keydown)="onFindInputKeydown($event)"
      />
      <span class="find-count" data-testid="preview-find-status" role="status" aria-live="polite" aria-atomic="true">
        @if (textSearchState.availability === 'stale') {
          Location changed
        } @else if (textSearchState.matches.length) {
          {{ textSearchState.activeIndex + 1 }} of {{ textSearchState.matches.length }}
        } @else if (textSearchState.availability === 'searching') {
          Searching…
        } @else {
          0 of 0
        }
        @if (
          textSearchState.message && (textSearchState.availability !== 'available' || !textSearchState.matches.length)
        ) {
          <span class="visually-hidden">. {{ textSearchState.message }}</span>
        }
      </span>
      <button
        type="button"
        class="find-action"
        aria-label="Previous match"
        [disabled]="textSearchState.availability === 'stale' || !textSearchState.matches.length"
        (click)="moveTextMatch(-1)"
      >
        <mat-icon>keyboard_arrow_up</mat-icon>
      </button>
      <button
        type="button"
        class="find-action"
        aria-label="Next match"
        [disabled]="textSearchState.availability === 'stale' || !textSearchState.matches.length"
        (click)="moveTextMatch(1)"
      >
        <mat-icon>keyboard_arrow_down</mat-icon>
      </button>
      <button type="button" class="find-action" aria-label="Close find" (click)="closeFind()">
        <mat-icon>close</mat-icon>
      </button>
      @if (
        textSearchState.message && (textSearchState.availability !== 'available' || !textSearchState.matches.length)
      ) {
        <p class="find-status" aria-hidden="true">{{ textSearchState.message }}</p>
      }
      @if ((textSearchState.availability === 'stale' || textSearchState.availability === 'auth-error') && findQuery) {
        <button type="button" class="find-search-again" (click)="searchAgain()">Search again</button>
      }
    </form>
  }
  @if (isLoading || isRotating) {
    <mat-progress-bar data-testid="preview-loading" mode="indeterminate" class="loading-progress"></mat-progress-bar>
  }

  <div
    #pdfContainer
    class="pdf-container"
    data-testid="preview-pdf-container"
    tabindex="-1"
    aria-label="PDF preview"
    [class.scale-panel-docked-left]="scalePanelDock === 'left'"
    [class.scale-panel-docked-right]="scalePanelDock === 'right'"
    [class.text-tool]="altHeld && !activeTool"
  >
    <!-- Canvas must always exist for ViewChild reference, hide it visually when loading -->
    <!-- data-page-rotation and data-page-size state how the page is rendered now (Issue #2445).
         They let an end-to-end test wait for a turn to be painted and convert between screen
         pixels and PDF points without guessing. -->
    <div
      #canvasWrapper
      class="canvas-wrapper"
      data-testid="preview-canvas-wrapper"
      [class.hidden]="!isPreviewReady"
      [attr.data-page-rotation]="renderedPageRotation"
      [attr.data-page-size]="renderedPageSize"
    >
      <canvas #pdfCanvas data-testid="preview-canvas"></canvas>
    </div>

    <!-- Spotlight overlay for image object search (Issue #292) -->
    <!-- Sibling of canvas-wrapper so it can fill the full pdf-container viewport -->
    @if (highlightBox && isPreviewReady) {
      <app-spotlight-overlay
        [highlightBox]="highlightBox"
        [highlightRegions]="spotlightRegions"
        [dimmedBox]="dimmedHighlightBox"
        [fadeChanges]="spotlightFade"
        [wrapperWidth]="spotlightWrapperWidth"
        [wrapperHeight]="spotlightWrapperHeight"
        [canvasWidth]="spotlightCanvasWidth"
        [canvasHeight]="spotlightCanvasHeight"
        [canvasScale]="spotlightCanvasScale"
        [canvasOffsetX]="spotlightCanvasOffsetX"
        [canvasOffsetY]="spotlightCanvasOffsetY"
        [featherPercent]="spotlightFeatherPct"
        [dismissOnEscape]="!findOpen"
        (dismiss)="onDismissHighlight()"
      ></app-spotlight-overlay>
    }

    @if (textHighlightBoxes.length && isPreviewReady) {
      <app-text-highlight-overlay
        [boxes]="textHighlightBoxes"
        [normalized]="textHighlightBoxesNormalized"
        [canvasWidth]="spotlightCanvasWidth"
        [canvasHeight]="spotlightCanvasHeight"
        [canvasScale]="spotlightCanvasScale"
        [canvasOffsetX]="spotlightCanvasOffsetX"
        [canvasOffsetY]="spotlightCanvasOffsetY"
      ></app-text-highlight-overlay>
    }

    <!-- OCR text selection overlay for clipboard copy and chat context (Issue #1745) -->
    @if (currentTextIndex && isPreviewReady && !activeTool) {
      <app-text-selection-overlay
        [pageIndex]="currentTextIndex"
        [projectId]="currentPage?.getArchitecturalPlanId() || ''"
        [fileId]="currentPageFileId"
        [fileName]="currentPage?.getTitle() || ''"
        [pageNumber]="currentPage?.getPageNumber() || 0"
        [canvasWidth]="spotlightCanvasWidth"
        [canvasHeight]="spotlightCanvasHeight"
        [canvasScale]="spotlightCanvasScale"
        [canvasOffsetX]="spotlightCanvasOffsetX"
        [canvasOffsetY]="spotlightCanvasOffsetY"
        [disabled]="isLoading || !!activeTool"
      ></app-text-selection-overlay>
    }

    <!-- Select area: the shared page-region selection for measuring and chat, and restored regions
         from chat links (Issues #1747, #2542) -->
    @if (isPreviewReady && (regionChatStore.active || regionChatStore.restored)) {
      <app-region-chat-surface
        [canvasWidth]="spotlightCanvasWidth"
        [canvasHeight]="spotlightCanvasHeight"
        [canvasOffsetX]="spotlightCanvasOffsetX"
        [canvasOffsetY]="spotlightCanvasOffsetY"
      ></app-region-chat-surface>
    }

    <!-- Ruler: measuring, and the reference line of a scale calibration (Issues #1212, #2442, #2542) -->
    @if (measureMode && isPreviewReady) {
      <app-measure-overlay
        [mode]="measureMode"
        [areaShape]="overlayAreaShape"
        [chatReview]="areaChatReview"
        [sheetHosted]="true"
        [canvasWidth]="spotlightCanvasWidth"
        [canvasHeight]="spotlightCanvasHeight"
        [canvasScale]="spotlightCanvasScale"
        [canvasOffsetX]="spotlightCanvasOffsetX"
        [canvasOffsetY]="spotlightCanvasOffsetY"
        [currentScale]="currentScale"
        [drawingScaleText]="scale.automatic.scaleText"
        [drawingScaleFactor]="scale.effective.scaleFactor"
        [pageSizeCorrectionFactor]="scale.correctionFactor"
        [pageSizePoints]="scale.displayedSize"
        [pageRotation]="scale.rotation"
        [scaleSourceLabel]="scale.effective.label"
        [manuallyCalibrated]="scale.effective.source === 'manual'"
        [presetLine]="scale.presetLine"
        (measurementClear)="onMeasurementClear()"
        (calibrationLineChange)="onCalibrationLineChange($event)"
        (useAsScaleReference)="onUseAsScaleReference($event)"
        (areaUseInChat)="onAreaUseInChat($event)"
        (areasCleared)="onAreasCleared($event)"
      ></app-measure-overlay>
    }

    <!-- Loading state with Material spinner and card (matches app-loading component) -->
    @if (isLoading) {
      <div class="loading-container">
        <mat-card class="loading-card">
          <mat-card-content>
            <div class="loading-content">
              <mat-spinner diameter="40"></mat-spinner>
              <h3>Loading PDF Preview</h3>
              <p class="loading-message">Please wait...</p>
            </div>
          </mat-card-content>
        </mat-card>
      </div>
    }

    @if (!isLoading && pdfRenderFailure) {
      <div
        class="preview-error-container"
        data-testid="preview-render-error"
        role="alert"
        [attr.data-error-kind]="pdfRenderFailure.kind"
      >
        <mat-card class="preview-error-card">
          <mat-card-content>
            <mat-icon aria-hidden="true">error_outline</mat-icon>
            <h3>PDF preview unavailable</h3>
            <p>{{ pdfRenderFailure.message }}</p>
            <button type="button" mat-button (click)="retryPdfPreview()">Retry preview</button>
          </mat-card-content>
        </mat-card>
      </div>
    }
  </div>

  @if (!isLoading && (page$ | async) === null) {
    <div class="no-page-selected" data-testid="preview-no-page" role="status" aria-live="polite">
      <mat-icon>description</mat-icon>
      <p>Select a page to view its preview</p>
    </div>
  }

  <!--
    The measurement result (Issue #2542): a panel docked in a bottom corner, or a sheet along the
    bottom on a phone. It must stay still while the page scrolls, and the measure overlay scrolls
    with the page, so the overlay's result template is drawn here, in the Preview's own container,
    beside the toolbar and the Freehand panel.

    The template is read inside this block on purpose. The overlay lives in a block of its own
    further up, and Angular refreshes blocks in order, after this template's own bindings. Read
    out here in the open, the overlay's state would be one step stale: the result would show or
    go a refresh late, and Angular reports that as a value changed after it was checked.
  -->
  @if (measureMode && isPreviewReady) {
    <ng-container [ngTemplateOutlet]="measureResultTemplate" />
  }

  @if (isPreviewReady) {
    <app-region-chat-panel></app-region-chat-panel>
  }

  <!-- Calibrate scale: the calibration form, and the details of a saved calibration (Issue #2442) -->
  @if (isPreviewReady && scale.panelView; as scalePanelView) {
    <app-scale-calibration-panel
      [view]="scalePanelView"
      [line]="scale.draftLine"
      [pageSize]="scale.pageSize"
      [correctionFactor]="scale.correctionFactor"
      [automatic]="scale.automatic"
      [state]="scale.state"
      [busy]="scale.busy"
      [serverMessage]="scale.serverMessage"
      [serverWarnings]="scale.serverWarnings"
      [comparing]="scale.comparing"
      [history]="scale.history"
      [placement]="scale.panelPlacement"
      (apply)="onApplyCalibration($event)"
      (cancelCalibration)="onCancelCalibration()"
      (placeLine)="onPlaceCalibrationLine()"
      (redrawLine)="onRedrawCalibrationLine()"
      (recalibrate)="onRecalibrateFromDetails()"
      (resetToAutomatic)="resetScaleCalibration()"
      (toggleCompare)="scale.toggleCompare()"
      (closeDetails)="onCloseScaleDetails()"
      (movePanel)="scale.movePanel()"
    ></app-scale-calibration-panel>
  }

  <!-- PDF Toolbar - only show when PDF is loaded -->
  @if (isPreviewReady) {
    <app-pdf-toolbar
      [currentScale]="currentScale"
      [baseScale]="baseScale"
      [fitMode]="fitMode"
      [isRotating]="isRotating"
      [canEdit]="canEditProject"
      [interaction]="interaction"
      [areaShape]="areaShape"
      [scaleStatus]="scale.toolbarStatus"
      [scaleSummary]="scale.summary"
      [comparingScale]="scale.comparing"
      (action)="onToolbarAction($event)"
    ></app-pdf-toolbar>
  }

  <!-- Live region for announcing camera focus framing to assistive technologies without moving focus -->
  <div class="visually-hidden" role="status" aria-live="polite" aria-atomic="true">
    {{ focusAnnouncement }}
  </div>
</div>
